import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  AlertTriangle, Zap, Users, Cloud, TreePine, Cpu, CheckCircle
} from "lucide-react";

const EVENT_ICONS = {
  economic: Zap,
  hr: Users,
  competitive: AlertTriangle,
  operational: Cloud,
  regulatory: TreePine,
  technology: Cpu
};

export default function EventsSection({ game, team, onRefresh }) {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (game?.id) {
      fetchEvents();
    }
  }, [game?.id, game?.current_quarter]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/games/${game.id}/events`);
      setEvents(response.data);
      
      // Check for existing responses
      // In a real app, you'd fetch these from the backend
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = async (eventId, choiceId) => {
    if (!team?.id) return;
    
    setSubmitting(prev => ({ ...prev, [eventId]: true }));
    
    try {
      await axios.post(
        `${API}/teams/${team.id}/events/${eventId}/respond?choice_id=${choiceId}`
      );
      
      setResponses(prev => ({ ...prev, [eventId]: choiceId }));
      toast.success(t("decisions.responseSubmitted"));
      onRefresh?.();
    } catch (error) {
      toast.error("Failed to submit response");
    } finally {
      setSubmitting(prev => ({ ...prev, [eventId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <div key={i} className="h-48 bg-slate-200 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">{t("decisions.noEvents")}</h3>
          <p className="text-slate-500">
            {t("decisions.noEventsDesc")}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <AlertTriangle className="w-5 h-5 text-amber-600" />
        <p className="text-sm text-amber-800">
          <strong>{events.length} {t("decisions.specialEvents")}</strong> {t("decisions.specialEventsDesc")}
        </p>
      </div>

      {events.map((event) => (
        <EventCard
          key={event.id}
          event={event}
          selectedChoice={responses[event.id]}
          onSelect={(choiceId) => handleResponse(event.id, choiceId)}
          submitting={submitting[event.id]}
        />
      ))}
    </div>
  );
}

function EventCard({ event, selectedChoice, onSelect, submitting }) {
  const { t } = useTranslation();
  const [localChoice, setLocalChoice] = useState(selectedChoice);
  const Icon = EVENT_ICONS[event.event_type] || AlertTriangle;

  const typeColors = {
    economic: "bg-amber-100 text-amber-700",
    hr: "bg-blue-100 text-blue-700",
    competitive: "bg-rose-100 text-rose-700",
    operational: "bg-slate-100 text-slate-700",
    regulatory: "bg-emerald-100 text-emerald-700",
    technology: "bg-purple-100 text-purple-700"
  };

  const handleSubmit = () => {
    if (localChoice) {
      onSelect(localChoice);
    }
  };

  return (
    <Card className="border-l-4 border-l-amber-500" data-testid={`event-${event.id}`}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Icon className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <CardTitle className="text-lg">{event.title}</CardTitle>
              <Badge className={`mt-1 ${typeColors[event.event_type] || typeColors.operational}`}>
                {event.event_type.charAt(0).toUpperCase() + event.event_type.slice(1)}
              </Badge>
            </div>
          </div>
          {selectedChoice && (
            <Badge className="bg-emerald-100 text-emerald-700">
              <CheckCircle className="w-3 h-3 mr-1" />
              {t("decisions.responded")}
            </Badge>
          )}
        </div>
        <CardDescription className="mt-3 text-base text-slate-600">
          {event.description}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {selectedChoice ? (
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
            <p className="text-sm text-emerald-800">
              <strong>{t("decisions.yourResponse")}:</strong>{" "}
              {event.choices.find(c => c.id === selectedChoice)?.text || "Unknown"}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm font-medium text-slate-700">{t("decisions.chooseResponse")}</p>
            <RadioGroup
              value={localChoice || ""}
              onValueChange={setLocalChoice}
              className="space-y-3"
            >
              {event.choices.map((choice) => (
                <label
                  key={choice.id}
                  className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    localChoice === choice.id
                      ? "border-sky-500 bg-sky-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <RadioGroupItem value={choice.id} className="mt-0.5 mr-3" />
                  <div>
                    <p className="font-medium text-slate-800">{choice.text}</p>
                    <ImpactPreview impact={choice.impact} />
                  </div>
                </label>
              ))}
            </RadioGroup>

            <Button
              onClick={handleSubmit}
              disabled={!localChoice || submitting}
              className="w-full"
              data-testid={`submit-event-${event.id}`}
            >
              {submitting ? t("decisions.submitting") : t("decisions.submitResponse")}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ImpactPreview({ impact }) {
  const { t } = useTranslation();
  if (!impact || Object.keys(impact).length === 0) {
    return <p className="text-xs text-slate-500 mt-1">{t("decisions.noImmediateImpact")}</p>;
  }

  const impactItems = [];
  
  if (impact.fuel_cost_modifier) {
    const change = (impact.fuel_cost_modifier - 1) * 100;
    impactItems.push({
      label: t("decisions.fuelCosts"),
      value: `${change > 0 ? "+" : ""}${change.toFixed(0)}%`,
      positive: change < 0
    });
  }
  
  if (impact.quality_bonus) {
    impactItems.push({
      label: t("dashboard.quality"),
      value: `+${impact.quality_bonus}`,
      positive: true
    });
  }
  
  if (impact.quality_penalty) {
    impactItems.push({
      label: t("dashboard.quality"),
      value: `${impact.quality_penalty}`,
      positive: false
    });
  }
  
  if (impact.cost) {
    impactItems.push({
      label: t("decisions.costImpact"),
      value: `-$${impact.cost.toLocaleString()}`,
      positive: false
    });
  }
  
  if (impact.demand_bonus) {
    const change = (impact.demand_bonus - 1) * 100;
    impactItems.push({
      label: t("decisions.demandImpact"),
      value: `+${change.toFixed(0)}%`,
      positive: true
    });
  }

  if (impact.reliability_penalty) {
    impactItems.push({
      label: t("decisions.reliabilityImpact"),
      value: `${impact.reliability_penalty}`,
      positive: false
    });
  }

  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {impactItems.map((item) => (
        <span
          key={item.label}
          className={`text-xs px-2 py-0.5 rounded-full ${
            item.positive ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
          }`}
        >
          {item.label}: {item.value}
        </span>
      ))}
    </div>
  );
}
